import { useState, useEffect, useRef, useCallback } from 'react';
import * as Ably from 'ably';
import Confetti from './Confetti';

const API      = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = `${API}/ably/token`;

// ─── Ably client ──────────────────────────────────────────────────────────────
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
  return _ablyClient;
}
function useAblyChannel(channelName, handlers) {
  const ref = useRef(handlers);
  useEffect(() => { ref.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const ch   = client.channels.get(channelName);
    const subs = Object.keys(ref.current).map(ev => {
      const cb = (m) => ref.current[ev]?.(m.data);
      ch.subscribe(ev, cb);
      return { ev, cb };
    });
    return () => subs.forEach(({ ev, cb }) => ch.unsubscribe(ev, cb));
  }, [channelName]);
}

// ─── Colour palette ───────────────────────────────────────────────────────────
const C = {
  bg:      '#030a06',
  surface: '#071210',
  gold:    '#FFB300',
  goldDim: '#996900',
  green:   '#00A550',
  greenDim:'#005c2b',
  black:   '#111',
  symbol:  '#FFD700',
  main:    '#C0C0C0',
  minor:   '#CD7F32',
};

// ─── Mark icons (1–36) ────────────────────────────────────────────────────────
// A glyph for each Dream Book mark, shown on the reveal so the answer is clear
// even when the clue was a meaning (e.g. clue "Nourishment" → 🥚 EGG #4).
const MARK_ICON = {
  1:'👻', 2:'🕯️', 3:'⚰️', 4:'🥚', 5:'🦹', 6:'💪', 7:'👰', 8:'🤰', 9:'🐄',
  10:'🏠', 11:'🐕', 12:'👤', 13:'👮', 14:'👄', 15:'🐀', 16:'👧', 17:'🧑', 18:'🐎',
  19:'🪙', 20:'🤒', 21:'😈', 22:'👩', 23:'🧔🏿', 24:'💧', 25:'🦅', 26:'🧔🏼', 27:'🔥',
  28:'🐔', 29:'🙏', 30:'🐟', 31:'🦎', 32:'🥇', 33:'🏰', 34:'👶', 35:'🐐', 36:'👵',
};

// Render the mark as a bundled SVG image (Twemoji). Font emoji don't render on
// the Fire TV native WebView (no colour-emoji font), so we use <img> instead,
// which is identical across browsers and the native shell. Files live in
// client/public/marks/{1..36}.svg.
function MarkIcon({ n, size }) {
  if (!n) return null;
  return (
    <img
      src={`/marks/${n}.svg`}
      alt={MARK_ICON[n] || ''}
      style={{
        width: size, height: size, objectFit: 'contain', display: 'block',
        filter: `drop-shadow(0 0 22px ${C.gold}66)`,
      }}
    />
  );
}

// Generic emoji → bundled Twemoji SVG (so decorative emoji render on the native
// WebView too). Defaults to 1em so it scales with the surrounding font size.
const EMOJI_CP = {
  '🥇':'1f947', '🥈':'1f948', '🥉':'1f949', '🏆':'1f3c6', '👑':'1f451',
  '🎴':'1f3b4', '🔢':'1f522', '💰':'1f4b0',
};
function Emo({ e, size = '1em', style }) {
  const cp = EMOJI_CP[e];
  if (!cp) return <span style={style}>{e}</span>;
  return (
    <img src={`/emoji/${cp}.svg`} alt={e} draggable={false} style={{
      width: size, height: size, objectFit: 'contain',
      display: 'inline-block', verticalAlign: '-0.15em', ...style,
    }} />
  );
}
const MEDALS = [<Emo key="1" e="🥇" />, <Emo key="2" e="🥈" />, <Emo key="3" e="🥉" />];

// ─── Global styles + keyframes ────────────────────────────────────────────────
const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; }

  @keyframes dpFadeUp    { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
  @keyframes dpPop       { 0%{transform:scale(.6);opacity:0} 65%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
  @keyframes dpPulse     { 0%,100%{opacity:.75} 50%{opacity:1} }
  @keyframes dpGlow      { 0%,100%{filter:drop-shadow(0 0 12px currentColor)} 50%{filter:drop-shadow(0 0 30px currentColor)} }
  @keyframes dpFloat     { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
  @keyframes dpSlideIn   { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:translateX(0)} }
  @keyframes dpSlideUp   { from{opacity:0;transform:translateY(50px) scale(.9)} to{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes dpShake     { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)} 40%{transform:translateX(8px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)} }
  @keyframes dpSpin      { from{transform:rotate(0)} to{transform:rotate(360deg)} }
  @keyframes dpCountdown { from{stroke-dashoffset:0} to{stroke-dashoffset:283} }
  @keyframes dpScoreRise { from{opacity:0;transform:translateY(0) scale(1)} to{opacity:0;transform:translateY(-60px) scale(1.2)} }
  @keyframes dpWinnerIn  { from{opacity:0;transform:translateX(80px) scale(.85)} to{opacity:1;transform:translateX(0) scale(1)} }
  @keyframes dpBurst     {
    0%  { transform:scale(0) rotate(0deg);   opacity:1; }
    60% { transform:scale(1.4) rotate(180deg); opacity:.9; }
    100%{ transform:scale(2)   rotate(360deg); opacity:0; }
  }
  @keyframes dpCrown     { 0%,100%{transform:translateY(0) rotate(-5deg)} 50%{transform:translateY(-8px) rotate(5deg)} }
  @keyframes dpConfetti  { 0%{transform:translateY(-5vh) rotate(0)} 100%{transform:translateY(105vh) rotate(720deg);opacity:0} }
  @keyframes dpNumReveal { 0%{transform:scale(3) rotate(-15deg);opacity:0} 60%{transform:scale(.95) rotate(3deg)} 100%{transform:scale(1) rotate(0);opacity:1} }
  @keyframes dpTierBadge { 0%{transform:scaleX(0)} 100%{transform:scaleX(1)} }
  @keyframes dpOrb1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(60px,-40px) scale(1.15)} }
  @keyframes dpOrb2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-50px,35px) scale(.9)} }
  @keyframes dpMultiplierPop {
    0%  { opacity:0; transform:scale(0.2) rotate(-20deg); }
    60% { transform:scale(1.2) rotate(5deg); }
    100%{ opacity:1; transform:scale(1) rotate(0); }
  }

  .dp-winner-card { animation: dpWinnerIn .45s cubic-bezier(.34,1.4,.64,1) both; }
  .dp-score-rise  { animation: dpScoreRise 1.4s ease forwards; }
  .dp-number-reveal { animation: dpNumReveal .7s cubic-bezier(.34,1.2,.64,1) both; }
  .dp-multiplier-pop { animation: dpMultiplierPop .6s cubic-bezier(.34,1.4,.64,1) both; }
`;

// Confetti is now the shared canvas component (imported above).

// ─── Tier badge ───────────────────────────────────────────────────────────────
function TierBadge({ tier, size = 'md', animate = false }) {
  const cfg = {
    symbol: { color: C.symbol, label: '✦ SYMBOL', bg: 'rgba(255,215,0,.15)' },
    main:   { color: C.main,   label: '● MAIN',   bg: 'rgba(192,192,192,.12)' },
    minor:  { color: C.minor,  label: '○ MINOR',  bg: 'rgba(205,127,50,.12)' },
  };
  const t  = cfg[tier] || cfg.minor;
  const fs = size === 'lg' ? 'clamp(18px,2.2vw,28px)' : size === 'sm' ? 'clamp(13px,1.4vw,18px)' : 'clamp(15px,1.7vw,22px)';
  const pd = size === 'lg' ? 'clamp(6px,.8vh,10px) clamp(18px,2vw,28px)' : size === 'sm' ? '4px 12px' : '5px 14px';
  return (
    <span style={{
      display:'inline-block',
      background: t.bg,
      border: `1px solid ${t.color}44`,
      borderRadius: 8,
      color: t.color,
      fontSize: fs,
      fontFamily:"'Barlow Condensed',sans-serif",
      fontWeight: 700,
      letterSpacing: 3,
      padding: pd,
      animation: animate ? 'dpTierBadge .3s ease both' : 'none',
      transformOrigin: 'left center',
    }}>{t.label}</span>
  );
}

// ─── Circular countdown SVG ──────────────────────────────────────────────────
function CircleTimer({ secondsLeft, totalSecs = 5 }) {
  const r    = 64;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, secondsLeft / totalSecs);
  const dash = circ * (1 - frac);
  const hue  = Math.round(120 * frac);
  return (
    <svg width={150} height={150} style={{ transform:'rotate(-90deg)', display:'block', flexShrink:0 }}>
      <circle cx={75} cy={75} r={r} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth={10} />
      <circle
        cx={75} cy={75} r={r} fill="none"
        stroke={`hsl(${hue},90%,55%)`} strokeWidth={10}
        strokeDasharray={circ} strokeDashoffset={dash}
        strokeLinecap="round"
        style={{ transition:'stroke-dashoffset .9s linear, stroke .5s' }}
      />
      <text
        x={75} y={75}
        textAnchor="middle" dominantBaseline="central"
        style={{ transform:'rotate(90deg)', transformOrigin:'75px 75px',
          fill:'#fff', fontSize:44, fontFamily:"'Bebas Neue',sans-serif",
          fontWeight:700 }}
      >{secondsLeft}</text>
    </svg>
  );
}

// ─── Winner card ─────────────────────────────────────────────────────────────
function WinnerCard({ winner, rank, isSymbolFirst }) {
  const pts    = winner.points;
  const medals = MEDALS;
  const colors = {
    symbol: { bg:'rgba(255,215,0,.18)', border:C.symbol, glow:`0 0 32px rgba(255,215,0,.6)` },
    main:   { bg:'rgba(192,192,192,.14)', border:C.main, glow:`0 0 18px rgba(192,192,192,.35)` },
    minor:  { bg:'rgba(205,127,50,.14)', border:C.minor, glow:`0 0 14px rgba(205,127,50,.28)` },
  };
  const cl = colors[winner.tier] || colors.minor;
  return (
    <div className="dp-winner-card" style={{
      animationDelay: `${(rank - 1) * 0.08}s`,
      background: cl.bg,
      border: `1px solid ${cl.border}44`,
      borderRadius: 18,
      padding: 'clamp(12px,1.5vh,20px) clamp(18px,2.5vw,30px)',
      display:'flex', alignItems:'center', gap:'clamp(14px,2vw,22px)',
      boxShadow: rank === 1 ? cl.glow : 'none',
      marginBottom: 'clamp(8px,1vh,14px)',
      position:'relative', overflow:'hidden',
    }}>
      {rank === 1 && isSymbolFirst && (
        <div style={{
          position:'absolute', top:-10, right:14, fontSize:'clamp(28px,3.5vw,42px)',
          animation:'dpCrown 1.2s ease infinite',
        }}><Emo e="👑" /></div>
      )}
      <div style={{ fontSize:'clamp(28px,3.5vw,44px)' }}>{medals[rank - 1] || `#${rank}`}</div>
      <div style={{
        width:'clamp(52px,6vw,72px)', height:'clamp(52px,6vw,72px)', borderRadius:'50%',
        background:`linear-gradient(135deg, ${cl.border}33, ${cl.border}66)`,
        border:`2px solid ${cl.border}66`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(18px,2vw,26px)', color:'#fff', fontWeight:700,
        flexShrink:0,
      }}>{winner.initials || '??'}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
          fontSize:'clamp(18px,2.2vw,28px)', color:'#fff',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {winner.name}
        </div>
        <TierBadge tier={winner.tier} size="sm" />
      </div>
      <div style={{
        fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(26px,3.2vw,42px)',
        color: cl.border, textShadow:`0 0 18px ${cl.border}88`, flexShrink:0,
      }}>+{pts}</div>
    </div>
  );
}

// ─── 5-second Winner Window Overlay ──────────────────────────────────────────
function WinnerWindowOverlay({ windowData, winners, secondsLeft }) {
  if (!windowData) return null;
  const isSymbolFirst = winners[0]?.tier === 'symbol';
  return (
    <div style={{
      position:'absolute', inset:0, zIndex:200,
      background:'rgba(0,0,0,.78)',
      backdropFilter:'blur(6px)',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'flex-start',
      padding:'clamp(24px,4vh,48px) clamp(24px,4vw,60px) 0',
      animation:'dpFadeUp .3s ease both',
    }}>
      {/* Header */}
      <div style={{
        display:'flex', alignItems:'center', gap:'clamp(20px,3vw,40px)',
        marginBottom:'clamp(20px,3vh,36px)',
      }}>
        <CircleTimer secondsLeft={secondsLeft} totalSecs={5} />
        <div>
          <div style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(40px,5.5vw,72px)',
            color:C.gold, letterSpacing:5,
            textShadow:`0 0 30px ${C.gold}88`,
            lineHeight:1,
          }}>WINNER WINDOW OPEN!</div>
          <div style={{
            fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600,
            fontSize:'clamp(16px,2vw,26px)',
            color:'rgba(255,255,255,.65)', letterSpacing:2, marginTop:6,
          }}>
            {isSymbolFirst
              ? '✦ SYMBOL winner — more winners = BIGGER multiplier!'
              : 'Keep answering — every correct answer scores!'}
          </div>
        </div>
      </div>

      {/* Winner list */}
      <div style={{ width:'100%', maxWidth:'min(700px,90vw)', overflowY:'auto', maxHeight:'calc(100vh - 260px)' }}>
        {winners.map((w, i) => (
          <WinnerCard key={w.jid + i} winner={w} rank={i + 1} isSymbolFirst={isSymbolFirst} />
        ))}
      </div>

      {isSymbolFirst && winners.length === 1 && (
        <div style={{ position:'absolute', top:'30%', left:'50%', transform:'translate(-50%,-50%)', pointerEvents:'none' }}>
          {Array.from({length:14}).map((_, i) => (
            <div key={i} style={{
              position:'absolute',
              width:10, height:10,
              borderRadius:'50%',
              background: i % 2 === 0 ? C.gold : C.green,
              transform:`rotate(${i*25.7}deg) translateX(70px)`,
              animation:`dpBurst 1s ${i * 0.05}s ease forwards`,
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Number grid (Mode A hint) ────────────────────────────────────────────────
function NumberGrid({ correctNumber, revealed }) {
  return (
    <div style={{
      display:'grid', gridTemplateColumns:'repeat(9,1fr)', gap:'clamp(6px,min(1vh,1vw),14px)',
      // Fill the screen: the grid grows as large as fits so the numbers read from
      // across the room. Width is capped by viewport HEIGHT too — 9 cols × 4 rows of
      // square cells ≈ 9:4, so a 112vh width cap keeps the grid ~50vh tall, leaving
      // room for the clue block above without the 4th row clipping on short/embedded
      // viewports. The px cap is high enough that on 1080p+ the 112vh governor wins
      // (grid fills to ~50vh), so the numbers read across the room with minimal side
      // waste; it only bites on very wide ultrawide panels.
      maxWidth:'min(1500px,94vw,112vh)', margin:'0 auto', width:'100%',
    }}>
      {Array.from({length:36},(_,i) => i+1).map(n => {
        const isCorrect = revealed && n === correctNumber;
        return (
          <div key={n} style={{
            aspectRatio:'1', borderRadius:'clamp(8px,1vw,14px)',
            background: isCorrect
              ? `linear-gradient(135deg,${C.gold},#FF8C00)`
              : 'rgba(255,255,255,.07)',
            border: isCorrect
              ? `2px solid ${C.gold}`
              : '1px solid rgba(255,255,255,.12)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize: 'clamp(20px,min(3.4vh,3.6vw),66px)', fontWeight:700,
            color: isCorrect ? '#000' : 'rgba(255,255,255,.55)',
            boxShadow: isCorrect ? `0 0 28px ${C.gold}99` : 'none',
            transition:'all .4s cubic-bezier(.34,1.2,.64,1)',
            transform: isCorrect ? 'scale(1.15)' : 'scale(1)',
          }}>{n}</div>
        );
      })}
    </div>
  );
}

// ─── Leaderboard panel ────────────────────────────────────────────────────────
function LeaderboardPanel({ scores, teamScores }) {
  if (!scores || scores.length === 0) return null;
  const medals = MEDALS;
  const hasTeams = teamScores?.length > 0;
  return (
    <div style={{
      width:'clamp(240px,22vw,300px)',
      padding:'clamp(14px,1.8vh,20px) clamp(16px,1.6vw,22px)',
      background:'rgba(0,0,0,.92)',
      backdropFilter:'blur(24px)',
      border:'1px solid rgba(255,179,0,.28)',
      borderRadius:18,
      boxShadow:'0 24px 70px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,179,0,.12)',
    }}>
      {hasTeams && (
        <div style={{ marginBottom:'clamp(8px,1.2vh,14px)', paddingBottom:'clamp(8px,1.2vh,12px)',
          borderBottom:'1px solid rgba(255,179,0,.15)' }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
            fontSize:'clamp(11px,1.1vw,15px)', letterSpacing:4,
            color:'rgba(255,179,0,.45)', marginBottom:'clamp(6px,1vh,10px)' }}>TEAMS</div>
          {teamScores.map((t,i) => (
            <div key={t.id} style={{ display:'flex', alignItems:'center', gap:'clamp(6px,.8vw,10px)',
              padding:'clamp(3px,.5vh,6px) 0',
              borderBottom: i<teamScores.length-1?'1px solid rgba(255,255,255,.04)':'none' }}>
              <div style={{ width:'clamp(6px,.7vw,9px)', height:'clamp(6px,.7vw,9px)',
                borderRadius:'50%', background:t.color, flexShrink:0,
                boxShadow:`0 0 6px ${t.color}` }} />
              <span style={{ flex:1, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600,
                fontSize:'clamp(13px,1.4vw,18px)', color:i===0?C.gold:'rgba(255,255,255,.75)',
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.name}</span>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(14px,1.6vw,20px)',
                color:i===0?C.gold:(t.color||'rgba(255,255,255,.55)'), flexShrink:0 }}>{t.score}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{
        fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
        fontSize:'clamp(13px,1.4vw,18px)', letterSpacing:5,
        color:'rgba(255,179,0,.55)', marginBottom:'clamp(8px,1.2vh,14px)',
      }}>LEADERBOARD</div>
      {scores.slice(0,hasTeams?5:8).map((s,i) => (
        <div key={s.name+i} style={{
          display:'flex', alignItems:'center', gap:'clamp(8px,1vw,14px)',
          padding:'clamp(4px,.7vh,8px) 0',
          borderBottom: i < scores.length - 1 ? '1px solid rgba(255,255,255,.06)' : 'none',
          opacity: s.left ? .4 : 1,
        }}>
          <span style={{ fontSize:'clamp(16px,1.8vw,24px)', width:'clamp(24px,2.2vw,30px)', flexShrink:0 }}>
            {medals[i] || `${i+1}.`}
          </span>
          <span style={{
            flex:1, fontFamily:"'Barlow Condensed',sans-serif",
            fontWeight:600, fontSize:'clamp(13px,1.5vw,20px)', color:'rgba(255,255,255,.88)',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>{s.name}</span>
          <span style={{
            fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(15px,1.8vw,22px)',
            color: i === 0 ? C.gold : 'rgba(255,255,255,.55)', flexShrink:0,
          }}>{s.score}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Background orbs ──────────────────────────────────────────────────────────
function Orbs() {
  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
      <div style={{
        position:'absolute', width:'70vw', height:'70vw', top:'-30%', left:'-25%',
        background:'radial-gradient(circle, rgba(0,165,80,.08) 0%, transparent 65%)',
        animation:'dpOrb1 12s ease infinite',
      }} />
      <div style={{
        position:'absolute', width:'60vw', height:'60vw', bottom:'-25%', right:'-20%',
        background:'radial-gradient(circle, rgba(255,179,0,.07) 0%, transparent 65%)',
        animation:'dpOrb2 16s ease infinite 3s',
      }} />
    </div>
  );
}

// ─── Round end reveal overlay ─────────────────────────────────────────────────
function RoundEndReveal({ roundEnd, onDismiss }) {
  if (!roundEnd) return null;
  const { correctNumber, symbol, main, minor, tier, bonusAwarded, winners } = roundEnd;
  return (
    <div style={{
      position:'absolute', inset:0, zIndex:250,
      background:'rgba(0,0,0,.9)',
      backdropFilter:'blur(8px)',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      padding:'clamp(24px,4vh,48px) clamp(24px,4vw,60px)',
      animation:'dpFadeUp .4s ease both',
    }}>
      <div style={{ textAlign:'center', marginBottom:'clamp(20px,3vh,36px)' }}>
        <div style={{
          fontFamily:"'Barlow Condensed',sans-serif",
          fontSize:'clamp(16px,2vw,26px)', letterSpacing:8,
          color:'rgba(255,179,0,.55)', marginBottom:10,
        }}>THE MARK WAS</div>
        <div className="dp-number-reveal" style={{
          display:'flex', alignItems:'center', justifyContent:'center', gap:'clamp(14px,2.5vw,40px)',
        }}>
          <MarkIcon n={correctNumber} size="clamp(80px,11vw,170px)" />
          <div style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(140px,18vw,220px)', lineHeight:1,
            color:C.gold, textShadow:`0 0 80px ${C.gold}88, 0 0 160px ${C.gold}44`,
          }}>{correctNumber}</div>
        </div>
        <div style={{
          fontFamily:"'Bebas Neue',sans-serif",
          fontSize:'clamp(52px,7vw,90px)', letterSpacing:6,
          color:'#fff', marginTop:4,
        }}>{symbol}</div>
        <TierBadge tier={tier} size="lg" animate />
      </div>

      {bonusAwarded && (
        <div className="dp-multiplier-pop" style={{
          background:'rgba(255,215,0,.15)', border:`2px solid ${C.gold}`,
          borderRadius:18, padding:'clamp(14px,2vh,22px) clamp(28px,4vw,48px)',
          marginBottom:'clamp(16px,2.5vh,28px)', textAlign:'center',
        }}>
          <div style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(32px,4vw,54px)', color:C.gold, letterSpacing:4,
          }}>
            ✦ SYMBOL MULTIPLIER ×{bonusAwarded.multiplier}
          </div>
          <div style={{
            fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:'clamp(16px,1.8vw,24px)', color:'rgba(255,255,255,.75)', marginTop:4,
          }}>
            {bonusAwarded.name} earns +{bonusAwarded.bonusPoints} bonus pts!
          </div>
        </div>
      )}

      {winners.length > 0 && (
        <div style={{ display:'flex', gap:'clamp(8px,1.2vw,16px)', flexWrap:'wrap', justifyContent:'center', maxWidth:'min(800px,92vw)' }}>
          {winners.slice(0,6).map((w,i) => (
            <div key={w.jid+i} style={{
              background:'rgba(255,255,255,.07)', borderRadius:12,
              padding:'clamp(8px,1vh,14px) clamp(16px,2vw,24px)',
              display:'flex', alignItems:'center', gap:10,
              border:'1px solid rgba(255,255,255,.12)',
            }}>
              <TierBadge tier={w.tier} size="sm" />
              <span style={{
                fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
                fontSize:'clamp(16px,1.8vw,24px)',
              }}>{w.name}</span>
              <span style={{ color:C.gold, fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(18px,2vw,26px)' }}>
                +{w.points}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Meanings */}
      <div style={{ marginTop:'clamp(16px,2.5vh,28px)', textAlign:'center', maxWidth:'min(800px,90vw)' }}>
        <div style={{
          fontSize:'clamp(13px,1.5vw,19px)', color:'rgba(255,255,255,.35)', letterSpacing:4,
          fontFamily:"'Barlow Condensed',sans-serif", marginBottom:8,
        }}>MAIN MEANINGS</div>
        <div style={{
          color:'rgba(255,255,255,.65)', fontSize:'clamp(17px,2vw,26px)',
          fontFamily:"'Barlow',sans-serif", lineHeight:1.7,
        }}>
          {main?.slice(0,8).join(' · ')}
        </div>
      </div>
    </div>
  );
}

// ─── Mode A: Dream Number ─────────────────────────────────────────────────────
function DreamNumberScreen({ round, revealed }) {
  if (!round) return null;
  const tierColors = { symbol: C.symbol, main: C.main, minor: C.minor };
  const color = tierColors[round.tier] || C.minor;
  const len   = round.clue?.length || 0;
  const clueFs = len > 14
    ? 'clamp(40px,min(7vh,8vw),100px)'
    : len > 8
      ? 'clamp(50px,min(9vh,12vw),140px)'
      : 'clamp(60px,min(11vh,14vw),180px)';
  return (
    <div style={{
      flex:1, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      padding:'clamp(10px,2vh,28px) clamp(20px,3vw,48px) clamp(16px,3vh,40px)',
      gap:'clamp(12px,2.4vh,30px)',
    }}>
      {/* Prompt block — kept tight so the number grid below can dominate */}
      <div style={{
        display:'flex', flexDirection:'column', alignItems:'center',
        gap:'clamp(6px,1vh,14px)',
      }}>
        <div style={{
          fontFamily:"'Barlow Condensed',sans-serif",
          fontSize:'clamp(14px,1.8vw,24px)', letterSpacing:8,
          color:'rgba(255,255,255,.35)',
        }}>WHAT IS THE MARK?</div>

        <TierBadge tier={round.tier} size="lg" animate />

        <div style={{
          fontFamily:"'Bebas Neue',sans-serif",
          fontSize: clueFs,
          lineHeight:1, letterSpacing:4,
          color, textShadow:`0 0 50px ${color}66, 0 0 100px ${color}33`,
          animation:'dpFadeUp .5s cubic-bezier(.34,1.2,.64,1) both',
          textAlign:'center', maxWidth:'92vw',
        }}>{round.clue}</div>

        <div style={{
          fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
          fontSize:'clamp(16px,min(2.4vh,2.6vw),34px)', letterSpacing:6,
          color:C.gold, opacity:.85,
        }}>TEXT THE NUMBER 1 – 36</div>
      </div>

      <NumberGrid correctNumber={round.number} revealed={revealed} />
    </div>
  );
}

// ─── Mode B: Name the Mark ────────────────────────────────────────────────────
function NameTheMarkScreen({ round, revealed, revealedSymbol }) {
  if (!round) return null;
  return (
    <div style={{
      flex:1, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      padding:'clamp(20px,3vh,40px) clamp(20px,3vw,48px)',
      gap:'clamp(18px,2.8vh,36px)',
    }}>
      <div style={{
        fontFamily:"'Barlow Condensed',sans-serif",
        fontSize:'clamp(15px,1.8vw,24px)', letterSpacing:8,
        color:'rgba(255,255,255,.35)',
      }}>NAME THE MARK</div>

      <div style={{
        fontFamily:"'Bebas Neue',sans-serif",
        fontSize:'clamp(100px,min(22vh,24vw),280px)', lineHeight:.88,
        color:C.gold, textShadow:`0 0 100px ${C.gold}55, 0 0 200px ${C.gold}22`,
        animation:'dpFadeUp .4s cubic-bezier(.34,1.2,.64,1) both',
        letterSpacing:-4,
      }}>{round.number}</div>

      {revealed && revealedSymbol && (
        <div style={{
          display:'flex', flexDirection:'column', alignItems:'center', gap:'clamp(6px,1vh,14px)',
          animation:'dpPop .5s cubic-bezier(.34,1.4,.64,1) both',
        }}>
          <MarkIcon n={round.number} size="clamp(70px,10vw,150px)" />
          <div style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(52px,7vw,100px)', letterSpacing:4, color:'#fff',
          }}>{revealedSymbol}</div>
        </div>
      )}

      {/* Tier score guide */}
      <div style={{ display:'flex', gap:'clamp(12px,2vw,24px)' }}>
        {[
          { tier:'symbol', pts:1000, label:'Symbol Name' },
          { tier:'main',   pts:600,  label:'Main Meaning' },
          { tier:'minor',  pts:300,  label:'Minor Meaning' },
        ].map(({ tier, pts, label }) => {
          const colors = { symbol:C.symbol, main:C.main, minor:C.minor };
          return (
            <div key={tier} style={{
              background:'rgba(255,255,255,.06)', borderRadius:14,
              padding:'clamp(10px,1.5vh,18px) clamp(18px,2.5vw,32px)', textAlign:'center',
              border:`1px solid ${colors[tier]}33`,
            }}>
              <div style={{
                fontFamily:"'Bebas Neue',sans-serif",
                fontSize:'clamp(28px,3.5vw,48px)',
                color:colors[tier],
              }}>{pts} PTS</div>
              <div style={{
                fontFamily:"'Barlow Condensed',sans-serif",
                fontSize:'clamp(12px,1.3vw,18px)', letterSpacing:2,
                color:'rgba(255,255,255,.45)',
              }}>{label.toUpperCase()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Mode C: Dream Auction ────────────────────────────────────────────────────
function DreamAuctionScreen({ round, auctionSecs, auctionSecsLeft, bets, auctionRevealed }) {
  if (!round) return null;
  const tierColors = { symbol: C.symbol, main: C.main, minor: C.minor };
  const color = tierColors[round.tier] || C.minor;
  const pct   = auctionSecs > 0 ? Math.max(0, auctionSecsLeft / auctionSecs) : 0;
  const mult  = { symbol:'×3', main:'×2', minor:'×1.5' }[round.tier] || '×1.5';

  if (auctionRevealed) {
    return (
      <div style={{
        flex:1, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
        gap:'clamp(16px,2.5vh,30px)',
        padding:'clamp(20px,3vh,40px) clamp(20px,3vw,48px)',
      }}>
        <div style={{
          fontFamily:"'Barlow Condensed',sans-serif",
          fontSize:'clamp(15px,1.8vw,24px)', letterSpacing:6, color:'rgba(255,255,255,.35)',
        }}>THE ANSWER WAS</div>
        <div className="dp-number-reveal" style={{
          display:'flex', alignItems:'center', justifyContent:'center',
          gap:'clamp(16px,3vw,48px)',
        }}>
          <MarkIcon n={auctionRevealed.correctNumber} size="clamp(90px,13vw,200px)" />
          <div style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(160px,20vw,260px)',
            color:C.gold, textShadow:`0 0 100px ${C.gold}88`,
            lineHeight:.88,
          }}>{auctionRevealed.correctNumber}</div>
        </div>
        <div style={{
          fontFamily:"'Bebas Neue',sans-serif",
          fontSize:'clamp(52px,6.5vw,90px)', letterSpacing:4, color:'#fff',
        }}>
          {auctionRevealed.symbol}
        </div>
        {auctionRevealed.clue &&
          auctionRevealed.clue.toUpperCase() !== (auctionRevealed.symbol || '').toUpperCase() && (
          <div style={{
            fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:'clamp(15px,1.8vw,26px)', letterSpacing:2, color:'rgba(255,255,255,.5)',
          }}>
            YOU DREAMED: <span style={{ color:C.gold, fontWeight:700 }}>{auctionRevealed.clue}</span>
          </div>
        )}
        <div style={{
          background:'rgba(255,215,0,.12)', border:`2px solid ${C.gold}`,
          borderRadius:18, padding:'clamp(12px,1.8vh,22px) clamp(28px,4vw,52px)', textAlign:'center',
        }}>
          <div style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(32px,4vw,56px)', color:C.gold, letterSpacing:3,
          }}>
            MULTIPLIER {mult}
          </div>
          <div style={{
            fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:'clamp(15px,1.7vw,22px)', color:'rgba(255,255,255,.55)', letterSpacing:2,
          }}>
            {auctionRevealed.winners.length} / {auctionRevealed.totalBets} BET CORRECTLY
          </div>
        </div>
        {auctionRevealed.winners.slice(0,5).map((w,i) => (
          <div key={w.jid+i} style={{
            display:'flex', alignItems:'center', gap:'clamp(14px,2vw,24px)',
            background:'rgba(255,215,0,.1)', borderRadius:14,
            padding:'clamp(10px,1.5vh,18px) clamp(22px,3vw,40px)',
            border:`1px solid ${C.gold}44`, width:'min(580px,88vw)',
          }}>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(28px,3.5vw,46px)' }}>
              {MEDALS[i] || `#${i+1}`}
            </span>
            <span style={{
              flex:1, fontFamily:"'Barlow Condensed',sans-serif",
              fontWeight:700, fontSize:'clamp(20px,2.4vw,32px)',
            }}>{w.name}</span>
            <span style={{
              fontFamily:"'Bebas Neue',sans-serif",
              fontSize:'clamp(26px,3.2vw,44px)', color:C.gold,
            }}>
              +{w.winnings}
            </span>
          </div>
        ))}
      </div>
    );
  }

  const len    = round.clue?.length || 0;
  const clueFs = len > 12 ? 'clamp(80px,10vw,148px)' : 'clamp(110px,14vw,190px)';

  return (
    <div style={{
      flex:1, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      gap:'clamp(16px,2.5vh,28px)',
      padding:'clamp(20px,3vh,40px) clamp(20px,3vw,48px)',
    }}>
      <div style={{
        fontFamily:"'Barlow Condensed',sans-serif",
        fontSize:'clamp(15px,1.8vw,24px)', letterSpacing:6, color:'rgba(255,255,255,.35)',
      }}>DREAM AUCTION</div>

      <TierBadge tier={round.tier} size="lg" animate />

      <div style={{
        fontFamily:"'Bebas Neue',sans-serif",
        fontSize: clueFs,
        color, textShadow:`0 0 50px ${color}66`,
        animation:'dpFadeUp .5s ease both',
        textAlign:'center', lineHeight:1, maxWidth:'88vw',
      }}>{round.clue}</div>

      <div style={{
        fontFamily:"'Bebas Neue',sans-serif",
        fontSize:'clamp(22px,2.8vw,40px)', letterSpacing:4,
        color:'rgba(255,255,255,.45)', textAlign:'center',
      }}>TEXT: BET [AMOUNT] [NUMBER]&nbsp;&nbsp;·&nbsp;&nbsp;CORRECT WINS {mult}</div>

      {/* Countdown bar */}
      <div style={{ width:'min(600px,85vw)' }}>
        <div style={{
          height:'clamp(10px,1.2vh,16px)', borderRadius:8,
          background:'rgba(255,255,255,.12)', overflow:'hidden',
        }}>
          <div style={{
            height:'100%', borderRadius:8,
            width:`${pct * 100}%`,
            background:`linear-gradient(90deg, ${C.green}, ${C.gold})`,
            transition:'width .9s linear',
          }} />
        </div>
        <div style={{
          textAlign:'center', marginTop:'clamp(6px,.8vh,12px)',
          fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(36px,4.5vw,62px)',
          color: auctionSecsLeft <= 5 ? '#FF4B4B' : C.gold,
        }}>{auctionSecsLeft}s</div>
      </div>

      {/* Live bets */}
      {bets.length > 0 && (
        <div style={{
          display:'flex', flexWrap:'wrap', gap:'clamp(8px,1vw,14px)',
          justifyContent:'center',
          maxWidth:'min(800px,92vw)', maxHeight:'clamp(120px,18vh,200px)', overflowY:'auto',
        }}>
          {bets.slice(-14).map((b,i) => (
            <div key={b.name+i} style={{
              background:'rgba(255,255,255,.08)', borderRadius:10,
              padding:'clamp(6px,.8vh,10px) clamp(14px,1.8vw,22px)',
              display:'flex', gap:10, alignItems:'center',
              border:'1px solid rgba(255,255,255,.12)',
              animation:'dpSlideIn .3s ease both',
            }}>
              <span style={{
                fontFamily:"'Barlow Condensed',sans-serif",
                fontWeight:600, fontSize:'clamp(16px,1.8vw,24px)',
                color:'rgba(255,255,255,.85)',
              }}>{b.name}</span>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif",
                fontSize:'clamp(16px,1.8vw,22px)', color:'rgba(255,255,255,.4)' }}>→</span>
              <span style={{
                fontFamily:"'Bebas Neue',sans-serif",
                fontSize:'clamp(20px,2.2vw,30px)', color:C.gold,
              }}>{b.amount}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Waiting screen ───────────────────────────────────────────────────────────
function WaitingScreen({ game }) {
  const modeLabel = {
    dreamnumber:  'DREAM NUMBER',
    namethemark:  'NAME THE MARK',
    dreamauction: 'DREAM AUCTION',
  }[game?.mode] || 'DROP PAN';

  return (
    <div style={{
      flex:'1 0 auto',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      gap:'clamp(8px,2vh,22px)',
      padding:'clamp(10px,2.5vh,40px) clamp(16px,3vw,52px)',
    }}>
      <div style={{ fontSize:'clamp(48px,min(8vh,10vw),110px)', animation:'dpFloat 3s ease infinite' }}><Emo e="🎴" /></div>
      <div style={{
        fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(48px,min(8vh,10vw),120px)',
        color:C.gold, letterSpacing:10,
        textShadow:`0 0 60px ${C.gold}66`,
        animation:'dpFadeUp .6s ease both', lineHeight:1,
      }}>DROP PAN</div>
      <div style={{
        fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
        fontSize:'clamp(14px,min(2.5vh,2.4vw),30px)', letterSpacing:10, color:'rgba(255,179,0,.65)',
      }}>{modeLabel}</div>
      {game && (
        <div style={{
          fontFamily:"'Bebas Neue',sans-serif",
          fontSize:'clamp(30px,min(5vh,6vw),72px)',
          letterSpacing:8, color:'rgba(255,255,255,.55)',
        }}>{game.id}</div>
      )}
      <div style={{
        fontFamily:"'Barlow Condensed',sans-serif",
        fontSize:'clamp(13px,min(1.8vh,2vw),22px)', letterSpacing:3,
        color:'rgba(255,255,255,.3)', textAlign:'center',
      }}>
        Text /join {game?.id || ''} on WhatsApp to play
      </div>

      {/* Team list — shown when game is a team game */}
      {game?.teamMode && game?.teams?.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'clamp(6px,1vh,12px)',
          width:'100%', maxWidth:'min(640px,90vw)' }}>
          <div style={{
            fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
            fontSize:'clamp(11px,min(1.5vh,1.5vw),16px)', letterSpacing:4, color:'rgba(255,179,0,.5)',
            textTransform:'uppercase',
          }}>
            Team Game — Text /join {game.id} [team] to choose your side
          </div>
          <div style={{ display:'flex', gap:'clamp(8px,1.5vw,18px)', flexWrap:'wrap', justifyContent:'center' }}>
            {game.teams.map((t, i) => (
              <div key={t.id} style={{
                display:'flex', alignItems:'center', gap:'clamp(5px,.8vw,8px)',
                background:`${t.color}1A`, border:`2px solid ${t.color}66`,
                borderRadius:12, padding:'clamp(6px,1vh,12px) clamp(12px,2vw,20px)',
                fontFamily:"'Bebas Neue',sans-serif",
              }}>
                <div style={{ width:'clamp(8px,1vw,14px)', height:'clamp(8px,1vw,14px)',
                  borderRadius:'50%', background:t.color, flexShrink:0,
                  boxShadow:`0 0 10px ${t.color}88` }} />
                <span style={{ fontSize:'clamp(13px,min(2vh,2.2vw),24px)', color:t.color, letterSpacing:3 }}>
                  {i + 1}. {t.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Number grid preview — a proper 9-col grid so it fills the width and the
          numbers read across the room. Height-capped (90vh) so the 4th row never
          clips beneath the title block on short/embedded viewports. */}
      <div style={{
        display:'grid', gridTemplateColumns:'repeat(9,1fr)',
        gap:'clamp(5px,min(.8vh,.9vw),12px)',
        width:'100%', maxWidth:'min(820px,90vw,90vh)', margin:'0 auto',
      }}>
        {Array.from({length:36},(_,i)=>i+1).map(n => (
          <div key={n} style={{
            aspectRatio:'1', borderRadius:'clamp(7px,.9vw,12px)',
            background:'rgba(255,179,0,.09)', border:'1px solid rgba(255,179,0,.22)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:'clamp(15px,min(2.4vh,2.6vw),38px)', color:'rgba(255,179,0,.6)',
            animation:`dpFadeUp .4s ${n * 0.02}s ease both`,
          }}>{n}</div>
        ))}
      </div>
    </div>
  );
}

// ─── Game end screen ──────────────────────────────────────────────────────────
function GameEndScreen({ scores, teamScores }) {
  const medals = MEDALS;
  const hasTeams = teamScores?.length > 0;
  return (
    <div style={{
      flex:1, display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      gap:'clamp(14px,2.2vh,28px)',
      padding:'clamp(20px,3.5vh,48px) clamp(24px,4vw,60px)',
      position:'relative', overflowY:'auto',
    }}>
      <Confetti />
      <div style={{ fontSize:'clamp(60px,8vw,110px)' }}><Emo e="🏆" /></div>
      <div style={{
        fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(60px,9vw,130px)',
        color:C.gold, letterSpacing:10, lineHeight:1,
        textShadow:`0 0 80px ${C.gold}99`,
      }}>GAME OVER!</div>

      {/* Team standings */}
      {hasTeams && (
        <div style={{ width:'min(700px,92vw)', marginBottom:'clamp(8px,1.2vh,16px)' }}>
          <div style={{ textAlign:'center', fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:'clamp(12px,1.3vw,18px)', letterSpacing:6,
            color:'rgba(255,179,0,.5)', marginBottom:'clamp(10px,1.5vh,18px)' }}>
            TEAM STANDINGS
          </div>
          <div style={{ display:'flex', gap:'clamp(10px,1.5vw,20px)', justifyContent:'center', flexWrap:'wrap' }}>
            {teamScores.map((t,i) => (
              <div key={t.id} style={{
                background:`${t.color}18`,
                border:`2px solid ${t.color}${i===0?'99':'55'}`,
                borderRadius:16, padding:'clamp(14px,2vh,22px) clamp(18px,2.5vw,32px)',
                textAlign:'center', minWidth:'clamp(110px,14vw,180px)',
                animation:`dpSlideIn .5s ${i*.12}s ease both`,
              }}>
                <div style={{ fontSize:'clamp(24px,3vw,40px)', marginBottom:6 }}>
                  {MEDALS[i]||`#${i+1}`}
                </div>
                <div style={{ width:'clamp(8px,1vw,12px)', height:'clamp(8px,1vw,12px)', borderRadius:'50%',
                  background:t.color, margin:'0 auto clamp(6px,.8vh,10px)',
                  boxShadow:`0 0 14px ${t.color}` }} />
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700,
                  fontSize:'clamp(18px,2.2vw,32px)', color:i===0?C.gold:'#fff' }}>{t.name}</div>
                <div style={{ fontFamily:"'Bebas Neue',sans-serif",
                  fontSize:'clamp(28px,3.5vw,50px)', color:t.color,
                  textShadow:`0 0 20px ${t.color}99` }}>{t.score}</div>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif",
                  fontSize:'clamp(10px,1vw,13px)', color:'rgba(255,255,255,.35)' }}>
                  {t.playerCount} player{t.playerCount!==1?'s':''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Individual leaderboard */}
      <div style={{ width:'min(600px,90vw)' }}>
        {hasTeams && (
          <div style={{ textAlign:'center', fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:'clamp(11px,1.2vw,16px)', letterSpacing:5,
            color:'rgba(255,179,0,.4)', marginBottom:'clamp(8px,1.2vh,14px)' }}>
            INDIVIDUAL SCORES
          </div>
        )}
        {scores.slice(0,hasTeams?6:10).map((s,i) => (
          <div key={s.name+i} style={{
            display:'flex', alignItems:'center', gap:'clamp(14px,2vw,24px)',
            padding:'clamp(10px,1.5vh,18px) clamp(20px,3vw,36px)', marginBottom:'clamp(5px,.7vh,8px)',
            background: i === 0
              ? `linear-gradient(90deg, rgba(255,215,0,.22), rgba(255,215,0,.06))`
              : 'rgba(255,255,255,.06)',
            border: i === 0 ? `1px solid ${C.gold}44` : '1px solid rgba(255,255,255,.08)',
            borderRadius:14,
            animation:`dpSlideIn .4s ${(hasTeams?0.6:0)+i * 0.07}s ease both`,
            boxShadow: i === 0 ? `0 0 36px rgba(255,215,0,.22)` : 'none',
            opacity: s.left ? .4 : 1,
          }}>
            {s.teamColor && (
              <div style={{ width:'clamp(4px,.5vw,6px)', height:'clamp(28px,4vh,44px)',
                borderRadius:3, background:s.teamColor, flexShrink:0 }} />
            )}
            <span style={{ fontSize:'clamp(24px,3.2vw,46px)', flexShrink:0 }}>{medals[i] || `${i+1}.`}</span>
            <span style={{
              flex:1, fontFamily:"'Barlow Condensed',sans-serif",
              fontWeight:700, fontSize:'clamp(18px,2.4vw,36px)', color:'#fff',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            }}>{s.name}</span>
            <span style={{
              fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(22px,3vw,44px)',
              color: i === 0 ? C.gold : 'rgba(255,255,255,.7)', flexShrink:0,
            }}>{s.score} PTS</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Round header bar ─────────────────────────────────────────────────────────
function RoundHeader({ game, roundIdx }) {
  if (!game) return null;
  const current = (roundIdx ?? 0) + 1;
  const total   = game.totalRounds || 0;
  const modeIcon = { dreamnumber:'🔢', namethemark:'🎴', dreamauction:'💰' }[game.mode];
  const modeLabel = {
    dreamnumber:  'DREAM NUMBER',
    namethemark:  'NAME THE MARK',
    dreamauction: 'DREAM AUCTION',
  }[game.mode] || 'DROP PAN';
  return (
    <div style={{
      height:'clamp(62px,8vh,92px)', display:'flex', alignItems:'center',
      padding:'0 clamp(24px,3.5vw,56px)', gap:'clamp(16px,2.5vw,32px)',
      background:'rgba(0,0,0,.55)',
      borderBottom:'1px solid rgba(255,179,0,.12)',
      backdropFilter:'blur(10px)',
      zIndex:50, flexShrink:0,
    }}>
      <div style={{
        fontFamily:"'Bebas Neue',sans-serif",
        fontSize:'clamp(26px,3.2vw,46px)', letterSpacing:5,
        color:C.gold, whiteSpace:'nowrap', flexShrink:0,
      }}>DROP PAN</div>
      <div style={{
        fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600,
        fontSize:'clamp(14px,1.6vw,22px)',
        letterSpacing:3, color:'rgba(255,255,255,.45)',
        whiteSpace:'nowrap', flexShrink:0,
      }}>{modeIcon && <Emo e={modeIcon} style={{ marginRight:'.4em' }} />}{modeLabel}</div>
      <div style={{ flex:1, minWidth:0 }} />
      <div style={{
        fontFamily:"'Bebas Neue',sans-serif",
        fontSize:'clamp(22px,2.8vw,40px)',
        color:'rgba(255,255,255,.55)', letterSpacing:4,
        whiteSpace:'nowrap', flexShrink:0,
      }}>ROUND {current} / {total}</div>
      {/* Progress pips — capped at 20 to prevent overflow */}
      <div style={{ display:'flex', gap:'clamp(4px,.5vw,7px)', flexShrink:0 }}>
        {Array.from({length:Math.min(total,20)}).map((_,i) => (
          <div key={i} style={{
            width:'clamp(10px,1.2vw,16px)', height:'clamp(10px,1.2vw,16px)', borderRadius:'50%',
            background: i < current
              ? C.gold
              : i === current - 1 ? C.green : 'rgba(255,255,255,.15)',
            transition:'background .3s',
          }} />
        ))}
      </div>
    </div>
  );
}

// Reconstruct the live-round shape (matching the `round-start` Ably payload) from
// a fetched game, for mid-round join recovery. Prefers the server's answer-safe
// `currentRoundPublic` snapshot (production/sanitized); falls back to the raw
// `rounds` map on the unsanitized admin/dev response. Returns null when no round
// is active (waiting / between rounds / completed).
function deriveCurrentRound(game) {
  if (!game || game.status !== 'active') return null;
  if (game.currentRoundPublic) {
    return { totalRounds: game.totalRounds, ...game.currentRoundPublic };
  }
  const idx = game.currentRound;
  if (idx == null || idx < 0) return null;
  const r = game.rounds?.[idx];
  if (!r || r.status !== 'active') return null;
  return {
    roundIdx:    idx,
    totalRounds: game.totalRounds,
    mode:        game.mode,
    number:      game.mode === 'namethemark' ? r.number : null,
    clue:        game.mode !== 'namethemark' ? r.clue : null,
    tier:        r.tier,
    auctionSecs: game.mode === 'dreamauction' ? game.settings?.auctionBettingSecs : null,
  };
}

// ─── Main board ───────────────────────────────────────────────────────────────
export default function DropPanBoard({ gameId, onExit, tvMode = false, onGameCompleted }) {
  const [game,           setGame]           = useState(null);
  const [round,          setRound]          = useState(null);
  const [roundIdx,       setRoundIdx]       = useState(-1);
  const [winners,        setWinners]        = useState([]);
  const [windowData,     setWindowData]     = useState(null);
  const [secondsLeft,    setSecondsLeft]    = useState(5);
  const [scores,         setScores]         = useState([]);
  const [teamScores,     setTeamScores]     = useState([]);
  const [roundEnd,       setRoundEnd]       = useState(null);
  const [gameEnd,        setGameEnd]        = useState(null);
  const [auctionSecsLeft,setAuctionSecsLeft]= useState(0);
  const [bets,           setBets]           = useState([]);
  const [auctionRevealed,setAuctionRevealed]= useState(null);
  const [revealedSymbol, setRevealedSymbol] = useState(null);
  const [lbVisible,      setLbVisible]      = useState(false);

  useEffect(() => {
    fetch(`${API}/droppan/${gameId}`)
      .then(r => r.ok ? r.json() : null)
      .then(g => {
        if (!g) return;
        setGame(g);
        // ── Mid-round join recovery ──────────────────────────────────────────
        // If this screen mounts while a round is already live (a long-running TV
        // switching into an in-progress game), the transient `round-start` event
        // has already fired and will never replay. Rehydrate the round from the
        // fetched game so the board shows the live round immediately instead of a
        // blank mode screen. `currentRoundPublic` is the server's answer-safe
        // snapshot (production); `rounds` is present on the unsanitized admin/dev
        // response. Live Ably events override this as the game progresses.
        const r = deriveCurrentRound(g);
        if (r) {
          setRoundIdx(r.roundIdx);
          setRound(r);
          if (r.auctionSecsLeft != null) setAuctionSecsLeft(r.auctionSecsLeft);
        }
      })
      .catch(() => {});
  }, [gameId]);

  // Periodically slide the scoreboard in from the top-right (like America Says):
  // first peek after 8s, then every 35s, visible for 9s each time.
  useEffect(() => {
    const show = () => { setLbVisible(true); setTimeout(() => setLbVisible(false), 9000); };
    const t  = setTimeout(show, 8000);
    const iv = setInterval(show, 35000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, []);

  const channel = game ? `droppan:game:${gameId}` : null;

  useAblyChannel(channel, {
    'game-state': (data) => {
      setGame(g => g ? { ...g, status: data.state } : g);
    },
    'round-start': (data) => {
      setRoundIdx(data.roundIdx);
      setRound(data);
      setWinners([]);
      setWindowData(null);
      setRoundEnd(null);
      setBets([]);
      setAuctionRevealed(null);
      setRevealedSymbol(null);
      if (data.auctionSecs) setAuctionSecsLeft(data.auctionSecs);
    },
    'round-winner': (data) => {
      setWinners(prev => {
        const exists = prev.some(w => w.jid === data.jid);
        if (exists) return prev;
        return [...prev, data];
      });
    },
    'window-open': (data) => {
      setWindowData(data);
      setSecondsLeft(data.windowSecs || 5);
    },
    'window-tick': (data) => {
      setSecondsLeft(data.secondsLeft);
    },
    'round-end': (data) => {
      setRoundEnd(data);
      setWindowData(null);
      setRevealedSymbol(data.symbol);
    },
    'score-update': (data) => {
      setScores(data.scores || []);
      if (data.teamScores?.length > 0) setTeamScores(data.teamScores);
    },
    'auction-tick': (data) => {
      setAuctionSecsLeft(data.secondsLeft);
    },
    'bet-placed': (data) => {
      setBets(prev => [...prev, data]);
    },
    'auction-reveal': (data) => {
      setAuctionRevealed(data);
    },
    'answer-revealed': (data) => {
      // Name the Mark: flash the symbol above the number.
      setRevealedSymbol(data.symbol);
      // Dream Number: show the full reveal overlay + highlight the correct cell.
      // (Dream Auction reveals via the 'auction-reveal' event instead.)
      if (data.mode === 'dreamnumber') {
        setRoundEnd(prev => prev || {
          correctNumber: data.correctNumber,
          symbol:        data.symbol,
          main:          data.main || [],
          minor:         data.minor || [],
          tier:          data.tier,
          winners:       [],
          bonusAwarded:  null,
        });
      }
    },
    'game-end': (data) => {
      setGameEnd(data);
      if (data.teamScores?.length > 0) setTeamScores(data.teamScores);
      if (tvMode && onGameCompleted) {
        setTimeout(onGameCompleted, 9000);
      }
    },
  });

  useEffect(() => {
    if (!roundEnd) return;
    const t = setTimeout(() => setRoundEnd(null), 5500);
    return () => clearTimeout(t);
  }, [roundEnd]);

  if (!game) {
    return (
      <div style={{
        height:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
        background:C.bg, color:'rgba(255,255,255,.35)',
        fontFamily:"'Barlow Condensed',sans-serif",
        fontSize:'clamp(18px,2.2vw,28px)', letterSpacing:6,
      }}>
        LOADING DROP PAN…
      </div>
    );
  }

  const isGameEnd = gameEnd || game.status === 'completed';

  return (
    <div style={{
      height:'100vh', width:'100vw', overflow:'hidden',
      background:C.bg,
      fontFamily:"'Barlow',sans-serif", color:'#fff',
      display:'flex', flexDirection:'column',
      position:'relative',
    }}>
      <style>{STYLES}</style>
      <Orbs />

      {!isGameEnd && game.status !== 'waiting' && (
        <RoundHeader game={game} roundIdx={roundIdx} />
      )}

      <div style={{ flex:1, display:'flex', flexDirection:'column', position:'relative',
        overflowX:'hidden', overflowY: (isGameEnd || game.status === 'waiting') ? 'auto' : 'hidden' }}>

        {isGameEnd ? (
          <GameEndScreen scores={gameEnd?.scores || scores} teamScores={gameEnd?.teamScores || teamScores} />
        ) : game.status === 'waiting' ? (
          <WaitingScreen game={game} />
        ) : game.mode === 'dreamnumber' ? (
          <DreamNumberScreen round={round} revealed={!!roundEnd} />
        ) : game.mode === 'namethemark' ? (
          <NameTheMarkScreen round={round} revealed={!!revealedSymbol} revealedSymbol={revealedSymbol} />
        ) : (
          <DreamAuctionScreen
            round={round}
            auctionSecs={round?.auctionSecs || game.settings?.auctionBettingSecs || 30}
            auctionSecsLeft={auctionSecsLeft}
            bets={bets}
            auctionRevealed={auctionRevealed}
          />
        )}

        {windowData && game.mode !== 'dreamauction' && (
          <WinnerWindowOverlay
            windowData={windowData}
            winners={winners}
            secondsLeft={secondsLeft}
          />
        )}

        {roundEnd && !windowData && (
          <RoundEndReveal roundEnd={roundEnd} onDismiss={() => setRoundEnd(null)} />
        )}
      </div>

      {scores.length > 0 && !isGameEnd && (
        <div style={{
          position:'fixed', right:'clamp(12px,1.5vw,24px)', top:'clamp(74px,9vh,108px)', zIndex:60,
          transform: lbVisible ? 'translateX(0)' : 'translateX(calc(100% + 40px))',
          transition: lbVisible
            ? 'transform .65s cubic-bezier(.34,1.56,.64,1), opacity .4s ease'
            : 'transform .5s cubic-bezier(.6,0,.4,1), opacity .4s ease',
          opacity: lbVisible ? 1 : 0,
          pointerEvents:'none',
        }}>
          <LeaderboardPanel scores={scores} teamScores={teamScores} />
        </div>
      )}

      {onExit && !tvMode && (
        <button
          onClick={onExit}
          style={{
            position:'absolute', top:14, right:18, zIndex:500,
            background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.14)',
            borderRadius:10, color:'rgba(255,255,255,.38)', cursor:'pointer',
            fontFamily:"'Barlow Condensed',sans-serif",
            fontSize:'clamp(12px,1.3vw,16px)', letterSpacing:3,
            padding:'clamp(10px,1.2vh,14px) clamp(16px,1.8vw,22px)',
            minHeight: 44,
          }}
        >EXIT</button>
      )}
    </div>
  );
}
